import {
  CHANNEL_TYPE_LABELS,
  type ChannelType,
  type RateRule,
  type RuleEffectType,
  type RuleScopeType,
} from '../dataProvider';

/**
 * Display + derived-state helpers for the Rate rules surface (D-rate-rules). The
 * lifecycle is DISPLAYED off an injected clock (the promoLifecycle idiom): a
 * rule's currently-live state is derived from its status plus its window, never a
 * stored engine write. The previews and the resolver are the engine's PROJECTION;
 * these helpers are presentation only.
 */

/**
 * The derived lifecycle state. `status` governs whether a rule is live at all
 * (draft / paused); for an `active` rule the window decides scheduled / live /
 * expired, measured against the injected clock.
 */
export type RuleLifecycle =
  | 'draft'
  | 'scheduled'
  | 'live'
  | 'expired'
  | 'paused';

export const ruleLifecycle = (rule: RateRule, now: number): RuleLifecycle => {
  if (rule.status === 'draft') return 'draft';
  if (rule.status === 'paused') return 'paused';
  // active: the window decides.
  if (rule.visibleFrom != null && now < Date.parse(rule.visibleFrom)) {
    return 'scheduled';
  }
  if (rule.visibleUntil != null && now > Date.parse(rule.visibleUntil)) {
    return 'expired';
  }
  return 'live';
};

export const LIFECYCLE_LABEL: Record<RuleLifecycle, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  live: 'Live',
  expired: 'Expired',
  paused: 'Paused',
};

export const LIFECYCLE_COLOR: Record<
  RuleLifecycle,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  draft: 'default',
  scheduled: 'info',
  live: 'success',
  expired: 'warning',
  paused: 'error',
};

/**
 * Suggested default priority per scope type, by SPECIFICITY (narrowest highest).
 * The authoring form prefills this, but the field is editable and REQUIRED, so a
 * rule never saves with a silent 0.
 */
export const DEFAULT_PRIORITY: Record<RuleScopeType, number> = {
  store: 100,
  store_list: 50,
  marketplace_category: 40,
  tag_marketplaces: 30,
  marketplace: 20,
  category: 15,
  tag: 10,
  market: 5,
};

/**
 * Scopes that REQUIRE a visibility window: a marketplace or category rule is a
 * broad rule, and a permanent broad rate change belongs in the base rates, not a
 * rule. Narrower scopes (store, store_list, tag, etc) may be permanent.
 */
export const scopeRequiresWindow = (scopeType: RuleScopeType): boolean =>
  scopeType === 'marketplace' || scopeType === 'category';

export const EFFECT_LABEL: Record<RuleEffectType, string> = {
  multiplier: 'Multiplier',
  absolute: 'Set rate',
  cap: 'Cap',
  floor: 'Floor',
};

/** "Multiplier x1.5" / "Cap at 4%" — the effect in words. */
export const formatEffect = (rule: RateRule): string => {
  switch (rule.effectType) {
    case 'multiplier':
      return `Multiplier x${rule.effectValue}`;
    case 'absolute':
      return `Set rate to ${rule.effectValue}%`;
    case 'cap':
      return `Cap at ${rule.effectValue}%`;
    case 'floor':
      return `Floor at ${rule.effectValue}%`;
    default:
      return `${rule.effectValue}`;
  }
};

const day = (iso: string): string => new Date(iso).toISOString().slice(0, 10);

/** "2026-12-01 to 2026-12-26" or "permanent" — the window as plain dates. */
export const formatWindow = (rule: RateRule): string => {
  if (rule.visibleFrom == null && rule.visibleUntil == null) return 'Permanent';
  const from = rule.visibleFrom ? day(rule.visibleFrom) : 'open';
  const to = rule.visibleUntil ? day(rule.visibleUntil) : 'open';
  return `${from} to ${to}`;
};

export const SCOPE_LABEL: Record<RuleScopeType, string> = {
  store: 'Store',
  marketplace: 'Marketplace',
  category: 'Category',
  marketplace_category: 'Marketplace in category',
  tag: 'Tag',
  tag_marketplaces: 'Tag on marketplaces',
  store_list: 'Store list',
  market: 'Market',
};

/** A readable summary of a rule's scope (ids / keys / channel labels). */
export const formatScope = (rule: RateRule): string => {
  const ref = rule.scopeRef;
  switch (rule.scopeType) {
    case 'store':
      return `Store #${ref}`;
    case 'marketplace':
      return `Marketplace: ${CHANNEL_TYPE_LABELS[ref as ChannelType]}`;
    case 'category':
      return `Category #${ref}`;
    case 'marketplace_category': {
      const r = ref as { channelType: ChannelType; categoryId: number };
      return `${CHANNEL_TYPE_LABELS[r.channelType]} in category #${r.categoryId}`;
    }
    case 'tag':
      return `Tag: ${ref}`;
    case 'tag_marketplaces': {
      const r = ref as { tag: string; marketplaces: ChannelType[] };
      const ms = r.marketplaces.map((m) => CHANNEL_TYPE_LABELS[m]).join(', ');
      return `Tag ${r.tag} on ${ms}`;
    }
    case 'store_list':
      return `${(ref as number[]).length} stores`;
    case 'market':
      return `Market: ${String(ref).toUpperCase()}`;
    default:
      return String(ref);
  }
};
